import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FilePreview = ({ preview, fileType, currentFileId, onRemove }) => {
  const showPreview = preview || currentFileId;
  
  if (!showPreview) return null;

  return (
    <div className="relative border rounded-lg p-4 bg-gray-50">
      {preview && fileType === 'photo' && (
        <img 
          src={preview} 
          alt="Preview" 
          className="w-32 h-32 object-cover rounded-lg mx-auto"
        />
      )}
      
      {!preview && currentFileId && fileType === 'photo' && (
        <div className="flex items-center justify-center w-32 h-32 bg-gray-200 rounded-lg mx-auto">
          <ImageIcon className="w-12 h-12 text-gray-400" />
        </div>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="absolute top-2 right-2"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
};

const FileHint = ({ fileType, maxSize, visible }) => {
  if (!visible) return null;

  const hints = {
    pdf: `Máximo ${maxSize}MB. Formato: PDF`,
    photo: `Máximo ${maxSize}MB. Formatos: JPG, PNG, WEBP`
  };

  return (
    <p className="text-xs text-gray-500">
      {hints[fileType] || hints.photo}
    </p>
  );
};

const FileUpload = ({ 
  label, 
  fileType = 'photo', 
  accept = 'image/*',
  maxSize = 5,
  currentFileId,
  onFileUploaded,
  onFileRemoved 
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const validateFileSize = (file) => {
    const maxBytes = maxSize * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`El archivo no puede superar ${maxSize}MB`);
      return false;
    }
    return true;
  };

  const createPreview = (file) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await axios.post(
      `${API}/files/upload?file_type=${fileType}`,
      formData,
      { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );

    return data;
  };

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!validateFileSize(file)) return;

    try {
      setUploading(true);
      createPreview(file);
      
      const data = await uploadFile(file);
      
      toast.success('Archivo subido correctamente');
      onFileUploaded(data.id, data.path);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  }, [fileType, maxSize, onFileUploaded, validateFileSize, createPreview, uploadFile]);

  const handleRemove = useCallback(() => {
    setPreview(null);
    onFileRemoved();
  }, [onFileRemoved]);

  const showUploadButton = !preview && !currentFileId;
  const showHint = !preview && !currentFileId;

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {showUploadButton && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => document.getElementById(`file-${fileType}`).click()}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Subiendo...' : 'Seleccionar archivo'}
          </Button>
          <input
            id={`file-${fileType}`}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}

      <FilePreview 
        preview={preview}
        fileType={fileType}
        currentFileId={currentFileId}
        onRemove={handleRemove}
      />

      <FileHint 
        fileType={fileType}
        maxSize={maxSize}
        visible={showHint}
      />
    </div>
  );
};

export default FileUpload;
